import { autocompletion } from '@codemirror/autocomplete';

import type { Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';

export interface VariableGroup {
  namespace: string;
  variables: { path: string; label: string }[];
}

function extractSchemaVariables(
  properties: Record<string, unknown>,
  prefix: string,
): { path: string; label: string }[] {
  const result: { path: string; label: string }[] = [];
  for (const [key, propDef] of Object.entries(properties)) {
    const fullPath = `${prefix}.${key}`;
    result.push({ path: fullPath, label: key });
    const prop = propDef as Record<string, unknown> | undefined;
    if (!prop) continue;
    if (prop.type === 'object' && prop.properties) {
      result.push(...extractSchemaVariables(prop.properties as Record<string, unknown>, fullPath));
    }
    if (prop.type === 'array' && prop.items) {
      const items = prop.items as Record<string, unknown>;
      if (items.properties) {
        result.push(
          ...extractSchemaVariables(items.properties as Record<string, unknown>, fullPath),
        );
      }
    }
  }
  return result;
}

const STANDARD_REVIEW_FIELDS = new Set([
  'reviewed_outcome',
  'outcome_matches',
  'reviewed_lead_data',
  'lead_data',
  'lead_data_matches',
  'reviewed_availability',
  'availability_data',
  'availability_matches',
  'discrepancies',
  'analysis',
  'confidence_score',
  'generated_transcript',
]);

function buildReviewVariables(
  reviewSchema?: Record<string, unknown>,
): { path: string; label: string }[] {
  const base = [
    { path: 'review.reviewed_outcome', label: 'reviewed_outcome' },
    { path: 'review.ai_analysis', label: 'ai_analysis' },
    { path: 'review.confidence_score', label: 'confidence_score' },
    { path: 'review.generated_transcript', label: 'generated_transcript' },
    { path: 'review.additional_review_data', label: 'additional_review_data (all custom fields)' },
  ];

  if (!reviewSchema) return base;

  const properties = (reviewSchema as { properties?: Record<string, unknown> }).properties ?? {};

  for (const [key, propDef] of Object.entries(properties)) {
    const schemaVars = extractSchemaVariables({ [key]: propDef }, 'review');

    if (STANDARD_REVIEW_FIELDS.has(key)) {
      base.push(...schemaVars);
    } else {
      // Additional review data: add both flattened and full paths
      base.push(...schemaVars);
      base.push(...extractSchemaVariables({ [key]: propDef }, 'review.additional_review_data'));
    }
  }

  return base;
}

export interface ApiNodeMeta {
  outputKey: string;
  sampleResponseSchema?: Record<string, unknown>;
}

function extractFlatPaths(
  obj: unknown,
  prefix: string,
  depth = 0,
): { path: string; label: string }[] {
  if (depth > 4 || typeof obj !== 'object' || obj === null) return [];
  const result: { path: string; label: string }[] = [];
  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const fullPath = `${prefix}.${key}`;
    result.push({ path: fullPath, label: key });
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      result.push(...extractFlatPaths(val, fullPath, depth + 1));
    }
    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
      result.push(...extractFlatPaths(val[0], fullPath, depth + 1));
    }
  }
  return result;
}

export interface ClassifierMeta {
  outputKey: string;
  taxonomy?: Record<string, unknown>;
}

export interface QualityMeta {
  outputKey: string;
  rubric?: Record<string, unknown>;
}

export interface AgentMeta {
  outputKey: string;
  outputSchema?: Record<string, unknown>;
}

export interface AnalyticsMeta {
  outputKey: string;
  metricKeys: string[];
}

/**
 * Build groups for run_classifier / run_quality outputs. Booleans go under
 * `<outputKey>.labels` (classifier) or `<outputKey>.flags` (quality),
 * non-booleans under `<outputKey>.data`. Mirrors the runtime split.
 */
function buildClassifierVariables(meta: ClassifierMeta): { path: string; label: string }[] {
  const base: { path: string; label: string }[] = [
    { path: `${meta.outputKey}.confidenceScore`, label: 'confidenceScore' },
    { path: `${meta.outputKey}.taxonomyVersion`, label: 'taxonomyVersion' },
  ];
  const props = (meta.taxonomy as { properties?: Record<string, unknown> } | undefined)?.properties;
  if (props) {
    for (const [key, propDef] of Object.entries(props)) {
      const bucket = (propDef as { type?: string })?.type === 'boolean' ? 'labels' : 'data';
      base.push({ path: `${meta.outputKey}.${bucket}.${key}`, label: `${bucket}.${key}` });
    }
  }
  return base;
}

function buildQualityVariables(meta: QualityMeta): { path: string; label: string }[] {
  const base: { path: string; label: string }[] = [
    { path: `${meta.outputKey}.reasoning`, label: 'reasoning' },
    { path: `${meta.outputKey}.confidenceScore`, label: 'confidenceScore' },
    { path: `${meta.outputKey}.rubricVersion`, label: 'rubricVersion' },
  ];
  const props = (meta.rubric as { properties?: Record<string, unknown> } | undefined)?.properties;
  if (props) {
    for (const [key, propDef] of Object.entries(props)) {
      const bucket = (propDef as { type?: string })?.type === 'boolean' ? 'flags' : 'data';
      base.push({ path: `${meta.outputKey}.${bucket}.${key}`, label: `${bucket}.${key}` });
    }
  }
  return base;
}

function buildAnalyticsVariables(meta: AnalyticsMeta): { path: string; label: string }[] {
  return meta.metricKeys.flatMap((mk) => [
    { path: `${meta.outputKey}.${mk}.count`, label: `${mk}.count` },
    { path: `${meta.outputKey}.${mk}.inbound`, label: `${mk}.inbound` },
    { path: `${meta.outputKey}.${mk}.outbound`, label: `${mk}.outbound` },
    { path: `${meta.outputKey}.${mk}.change`, label: `${mk}.change` },
  ]);
}

/**
 * An agent node spreads its output at the top level of `<outputKey>`, so the
 * schema the author drew is the shape they template against — no labels/data
 * wrapper like classifier/quality.
 */
function buildAgentVariables(meta: AgentMeta): { path: string; label: string }[] {
  const properties =
    meta.outputSchema && typeof meta.outputSchema === 'object'
      ? ((meta.outputSchema as { properties?: Record<string, unknown> }).properties ?? {})
      : {};
  return [
    ...Object.keys(properties).map((name) => ({
      path: `${meta.outputKey}.${name}`,
      label: name,
    })),
    { path: `${meta.outputKey}._meta.confidenceScore`, label: '_meta.confidenceScore' },
    { path: `${meta.outputKey}._meta.reasoning`, label: '_meta.reasoning' },
  ];
}

export function buildVariableGroups(
  reviewSchema?: Record<string, unknown>,
  apiNodes?: ApiNodeMeta[],
  classifierMetas?: ClassifierMeta[],
  qualityMetas?: QualityMeta[],
  agentMetas?: AgentMeta[],
  analyticsMetas?: AnalyticsMeta[],
): VariableGroup[] {
  return [
    {
      namespace: 'call',
      variables: [
        { path: 'call.id', label: 'id' },
        { path: 'call.direction', label: 'direction' },
        { path: 'call.channel', label: 'channel' },
        { path: 'call.modality', label: 'modality' },
        { path: 'call.status', label: 'status' },
        { path: 'call.outcome', label: 'outcome' },
        { path: 'call.duration', label: 'duration' },
        { path: 'call.phone_number', label: 'phone_number' },
        { path: 'call.lead_data', label: 'lead_data' },
        { path: 'call.availability_data', label: 'availability_data' },
        { path: 'call.callback_data', label: 'callback_data' },
        { path: 'call.shift_data', label: 'shift_data' },
      ],
    },
    {
      namespace: 'review',
      variables: buildReviewVariables(reviewSchema),
    },
    {
      namespace: 'contact',
      variables: [
        { path: 'contact.name', label: 'name' },
        { path: 'contact.phone', label: 'phone' },
      ],
    },
    {
      namespace: 'campaign',
      variables: [
        { path: 'campaign.name', label: 'name' },
        { path: 'campaign.id', label: 'id' },
      ],
    },
    {
      namespace: 'config',
      variables: [
        { path: 'config.notification_emails', label: 'notification_emails' },
        { path: 'config.high_intent_emails', label: 'high_intent_emails' },
      ],
    },
    {
      namespace: 'sms',
      variables: [
        { path: 'sms_sent', label: 'sms_sent' },
        { path: 'sms_not_sent', label: 'sms_not_sent' },
        { path: 'sms_sent_to', label: 'sms_sent_to' },
      ],
    },
    ...(apiNodes ?? []).map((node) => {
      const base: { path: string; label: string }[] = [
        { path: node.outputKey, label: node.outputKey },
      ];
      if (node.sampleResponseSchema) {
        base.push(...extractFlatPaths(node.sampleResponseSchema, node.outputKey));
      }
      return { namespace: node.outputKey, variables: base };
    }),
    ...(classifierMetas ?? []).map((meta) => ({
      namespace: `${meta.outputKey} (classifier)`,
      variables: buildClassifierVariables(meta),
    })),
    ...(qualityMetas ?? []).map((meta) => ({
      namespace: `${meta.outputKey} (quality)`,
      variables: buildQualityVariables(meta),
    })),
    ...(agentMetas ?? []).map((meta) => ({
      namespace: `${meta.outputKey} (agent)`,
      variables: buildAgentVariables(meta),
    })),
    ...(analyticsMetas ?? []).map((meta) => ({
      namespace: `${meta.outputKey} (analytics)`,
      variables: buildAnalyticsVariables(meta),
    })),
  ];
}

export function templateAutocomplete(variableGroups: VariableGroup[]) {
  const allCompletions: Completion[] = variableGroups.flatMap((g) =>
    g.variables.map((v) => ({
      label: v.path,
      type: 'variable',
      detail: g.namespace,
      section: { name: g.namespace, rank: 0 },
    })),
  );

  function completions(context: CompletionContext): CompletionResult | null {
    const before = context.state.doc.sliceString(0, context.pos);
    const triggerIdx = before.lastIndexOf('{{');
    if (triggerIdx === -1) return null;

    let between = before.slice(triggerIdx + 2);
    if (between.includes('}}')) return null;

    // Strip {{#if prefix so autocomplete works inside conditions
    const ifMatch = between.match(/^#if\s+/);
    let ifOffset = 0;
    if (ifMatch) {
      ifOffset = ifMatch[0].length;
      between = between.slice(ifOffset);
    }

    // Strip comparison operators (==, !=) — autocomplete the right-hand side too
    const compMatch = between.match(/.*(\|\||[?:]|==|!=)\s*/);
    let from: number;

    if (compMatch) {
      const opEnd = between.lastIndexOf(compMatch[1]) + compMatch[1].length;
      from =
        triggerIdx +
        2 +
        ifOffset +
        opEnd +
        (between.slice(opEnd).length - between.slice(opEnd).trimStart().length);
    } else {
      from = triggerIdx + 2 + ifOffset;
    }

    return { from, options: allCompletions, filter: true };
  }

  return autocompletion({
    override: [completions],
    activateOnTyping: true,
    activateOnCompletion: () => true,
    interactionDelay: 0,
  });
}
